import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import AddIcon from "@material-ui/icons/Add";
import RemoveIcon from "@material-ui/icons/Remove";
import { NumberInputEx } from "components/inputs";
import React, { useState } from "react";
import { useTranslate } from "react-admin";
import { Form } from "react-final-form";

const useStyles = makeStyles((theme) => ({
	content: {
		display: "flex",
		flexDirection: "column",
		gap: theme.spacing(2),
		padding: theme.spacing(3),
	},
	palletInfo: {
		marginBottom: theme.spacing(2),
	},
	quantityInput: {
		marginTop: theme.spacing(2),
	},
}));

export const BoxMovementModal = ({ open, onClose, onConfirm, pallet, boxMovements }) => {
	const classes = useStyles();
	const translate = useTranslate();

	const [countBoxes, setCountBoxes] = useState(1);

	const handleConfirm = () => {
		if (countBoxes > 0) {
			onConfirm({
				pallet_id: pallet.id,
				count_boxes: countBoxes,
			});
			handleClose();
		}
	};

	const handleClose = () => {
		setCountBoxes(1);
		onClose();
	};

	const handleIncrement = (maxBoxes) => {
		setCountBoxes((prev) => Math.min((prev || 0) + 1, maxBoxes));
	};

	const handleDecrement = () => {
		setCountBoxes((prev) => Math.max((prev || 0) - 1, 1));
	};

	if (!pallet) return null;

	const totalBoxes =
		pallet.available_quantity != null ? pallet.available_quantity : pallet.quantity || 0;

	// Calculate already selected boxes from this pallet
	const alreadySelected = boxMovements?.[pallet.id]?.count_boxes || 0;

	const maxBoxes = totalBoxes - alreadySelected;

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
			<DialogTitle>{translate("tab.picking_list.box_movement.move_boxes_from_pallet")}</DialogTitle>
			<DialogContent className={classes.content}>
				<Box className={classes.palletInfo}>
					<Typography variant="body1">
						<strong>{translate("tab.picking_list.box_movement.move_from_pallet")}</strong>{" "}
						{pallet.sscc || pallet.id}
					</Typography>

					<Typography variant="body2" color="textSecondary">
						{translate("tab.picking_list.box_movement.total_boxes")}: {totalBoxes}
					</Typography>
					{alreadySelected > 0 && (
						<Typography variant="body2" color="primary">
							{translate("tab.picking_list.box_movement.already_selected")}: {alreadySelected}
						</Typography>
					)}
					<Typography variant="body2" color={maxBoxes > 0 ? "textSecondary" : "error"}>
						{translate("tab.picking_list.box_movement.remaining_available")}: {maxBoxes}
					</Typography>
				</Box>

				<Form
					onSubmit={() => {}}
					initialValues={{ count_boxes: countBoxes }}
					render={() => (
						<Box display="flex" alignItems="center" gap={1}>
							<IconButton
								color="primary"
								onClick={handleDecrement}
								disabled={!countBoxes || countBoxes <= 1}
							>
								<RemoveIcon />
							</IconButton>
							<NumberInputEx
								source="count_boxes"
								label={translate("tab.picking_list.box_movement.fields.quantity_of_boxes_to_move")}
								min={1}
								max={maxBoxes}
								className={classes.quantityInput}
								onChange={(e) => {
									const val = parseInt(e.target.value) || 0;
									if (val === 0 || e.target.value === "") {
										setCountBoxes(0);
									} else if (val >= 1) {
										setCountBoxes(Math.min(val, maxBoxes));
									}
								}}
								fullWidth
								autoFocus
							/>
							<IconButton
								color="primary"
								onClick={() => handleIncrement(maxBoxes)}
								disabled={countBoxes >= maxBoxes}
							>
								<AddIcon />
							</IconButton>
						</Box>
					)}
				/>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} color="default">
					{translate("osfc.buttons.cancel")}
				</Button>
				<Button
					onClick={handleConfirm}
					color="primary"
					variant="contained"
					disabled={!countBoxes || countBoxes < 1 || countBoxes > maxBoxes || maxBoxes <= 0}
				>
					{translate("tab.picking_list.box_movement.buttons.add_movement")}
				</Button>
			</DialogActions>
		</Dialog>
	);
};
